/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  total: '',
  vat: '',
  discount: '',
  nettotal: '',
};

function PosCancel({ parent, callback }) {
  const mod = 'pos_cancel';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [image, setImage] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const open = () => {
    setLoading(true);

    FetchData('POST', `/${mod}/open`, app.token, {
      lang: app.lang,
      id: parent.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setList(result.list);
        setImage(result.image);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const approve = () => {
    Swal.fire({
      title: 'คุณต้องการอนุมัติรายการ\nใช่หรือไม่',
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'อนุมัติ',
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: attr.id,
          status: 'approve',
        }).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            callback();
            // showNotify(t(`alert.editcomplete`), "success");
          } else {
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const reject = () => {
    Swal.fire({
      title: 'คุณไม่ต้องการอนุมัติรายการ\nใช่หรือไม่',
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'ไม่อนุมัติ',
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: attr.id,
          status: 'reject',
        }).then((result) => {
          setLoading(true);
          if (result.status === 'ok') {
            callback();
            // showNotify(t(`alert.editcomplete`), "success");
          } else {
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const renderOrder = () => {
    return (
      <MDBox className='card' p={2}>
        <MDBox mb={2}>
          <MDTypography variant='h5'>Orders</MDTypography>
        </MDBox>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center' width={20}>
                  No.
                </TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell align='right' width={90}>
                  ราคา
                </TableCell>
                <TableCell align='right' width={90}>
                  จำนวน
                </TableCell>
                <TableCell align='left' width={50}>
                  หน่วย
                </TableCell>
                <TableCell align='right' width={90}>
                  รวม
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.price)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.unit_name}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderSummary = () => {
    return (
      <MDBox className='card' p={2} mt={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='summary table'>
            <TableBody>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    มูลค่ารวม :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    {Func.numberDisplay(attr.total, 2)} ฿
                  </MDTypography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    คูปอง :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {attr.discount_coupon * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(attr.discount_coupon, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    โปรโมชั่น :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {attr.discount * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(attr.discount, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    คืนสินค้า :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {attr.problem_type === 'return' ? (
                    <MDTypography variant='h4' className='nowrap'>
                      - {Func.numberDisplay(attr.return_total, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    ขอส่วนลด:
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {attr.discount_approve * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(attr.discount_approve, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    รวมทั้งสิ้น :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' color='info' className='nowrap'>
                    {Func.numberDisplay(attr.nettotal, 2)} ฿
                  </MDTypography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderImage = () => {
    if (image.length === 0) return null;

    return (
      <MDBox className='card' p={2}>
        <MDBox mb={2}>
          <MDTypography variant='h5'>Image</MDTypography>
        </MDBox>
        <Grid container spacing={2}>
          {image.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <img src={item.image} alt={item.title} width='100%' />
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    const grid = (label, value) => {
      return (
        <>
          <Grid item xs={4}>
            {label}
          </Grid>
          <Grid item xs={8}>
            {value}
          </Grid>
        </>
      );
    };
    return (
      <MDBox className='card' p={2}>
        <MDBox mb={2}>
          <MDTypography variant='h5'>Infomation</MDTypography>
        </MDBox>
        <Grid container spacing={1}>
          {grid('เลขที่ขอ', attr.code)}
          {grid('สาขา', attr.branch_name)}
          {grid('เลขที่บิล', attr.so_code)}
          {grid('POS', attr.cashier_name)}
          {grid('รอบ', attr.shift_code)}
          {grid('พนักงาน', attr.user_name)}
        </Grid>
      </MDBox>
    );
  };

  const renderButton = () => {
    return (
      <MDBox mb={2}>
        <Grid container>
          <Grid item xs={6}>
            <MDButton variant='gradient' color='error' onClick={reject}>
              <Icon>cancel</Icon>&nbsp;ไม่อนุมัติ
            </MDButton>
          </Grid>
          <Grid item xs={6} display='flex' justifyContent='flex-end'>
            <MDButton variant='gradient' color='success' onClick={approve}>
              <Icon>check_circle_outline</Icon>&nbsp;อนุมัติ
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox sx={{ minHeight: 600 }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {renderButton()}
          </Grid>
          <Grid item xs={12} md={4}>
            {renderInfo()}
          </Grid>
          <Grid item xs={12} md={8}>
            {renderOrder()}
            {renderSummary()}
          </Grid>
          <Grid item xs={12}>
            {renderImage()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    open();
  }, [parent]);

  return (
    <MDBox>
      {renderList()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

PosCancel.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  callback: PropTypes.func.isRequired,
};

export default PosCancel;
